import React, { useMemo } from 'react';
import {
  Card,
  Typography,
  Grid,
  Box,
  Stack,
  Chip,
  Button,
  IconButton,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { registerChart } from './chartSetup.js';
import { SlidersHorizontal, TrendingUp } from 'lucide-react';

registerChart();

function computeMovingAverage(data, windowSize = 5) {
  return data.map((_, idx) => {
    const start = Math.max(0, idx - windowSize + 1);
    const slice = data.slice(start, idx + 1);
    const avg = slice.reduce((acc, curr) => acc + curr, 0) / slice.length;
    return Number(avg.toFixed(2));
  });
}

function convertSamplesToRates(samples) {
  if (!samples || samples.length === 0) return [];
  const sorted = [...samples].sort((a, b) => a.timestamp - b.timestamp);

  const maxVal = Math.max(0, ...sorted.map((s) => s.sample));
  const isCumulative = maxVal > 5 && sorted.some((s, idx) => idx > 0 && s.sample >= sorted[idx - 1].sample);

  if (!isCumulative) {
    return sorted;
  }

  const rates = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      rates.push({ timestamp: sorted[i].timestamp, sample: 0 });
    } else {
      const dt = (sorted[i].timestamp - sorted[i - 1].timestamp) / 1000;
      const dv = sorted[i].sample - sorted[i - 1].sample;
      const rate = dt > 0 && dv >= 0 ? Number((dv / dt).toFixed(2)) : 0;
      rates.push({ timestamp: sorted[i].timestamp, sample: rate });
    }
  }
  return rates;
}

export const MessageRatesChart = ({
  stats = {},
  config = { display: 'chart', size: 'medium', range: '10m' },
  onOpenConfig = () => {},
}) => {
  const rangeLabels = {
    '1m': 'last minute',
    '10m': 'last ten minutes',
    '1h': 'last hour',
    '8h': 'last eight hours',
    '1d': 'last day',
  };

  const chartHeights = {
    small: 180,
    medium: 240,
    large: 340,
  };

  const deliverSamples = stats.deliverManualSamples || [];
  const ackSamples = stats.ackSamples || [];
  const publishSamples = stats.publishSamples || [];
  const redeliverSamples = stats.redeliverSamples || [];

  const { labels, publishData, deliverData, ackData, redeliverData, yMax, yStepSize } = useMemo(() => {
    const convertedPublish = convertSamplesToRates(publishSamples);
    const convertedDeliver = convertSamplesToRates(deliverSamples);
    const convertedAck = convertSamplesToRates(ackSamples);
    const convertedRedeliver = convertSamplesToRates(redeliverSamples);

    const timestampSet = new Set();
    convertedPublish.forEach((s) => timestampSet.add(s.timestamp));
    convertedDeliver.forEach((s) => timestampSet.add(s.timestamp));
    convertedAck.forEach((s) => timestampSet.add(s.timestamp));
    convertedRedeliver.forEach((s) => timestampSet.add(s.timestamp));

    let sortedTimestamps = Array.from(timestampSet).sort((a, b) => a - b);

    if (sortedTimestamps.length === 0) {
      const now = Date.now();
      const rangeSec =
        config.range === '1m' ? 60 :
        config.range === '10m' ? 600 :
        config.range === '1h' ? 3600 :
        config.range === '8h' ? 28800 : 86400;
      const stepSec =
        config.range === '1m' ? 5 :
        config.range === '10m' ? 30 :
        config.range === '1h' ? 60 :
        config.range === '8h' ? 240 : 720;
      for (let t = now - rangeSec * 1000; t <= now; t += stepSec * 1000) {
        sortedTimestamps.push(t);
      }
    }


    const publishMap = new Map(convertedPublish.map((s) => [s.timestamp, s.sample]));
    const deliverMap = new Map(convertedDeliver.map((s) => [s.timestamp, s.sample]));
    const ackMap = new Map(convertedAck.map((s) => [s.timestamp, s.sample]));
    const redeliverMap = new Map(convertedRedeliver.map((s) => [s.timestamp, s.sample]));

    const formatTime = (ts) => {
      const d = new Date(ts);
      const h = d.getHours().toString().padStart(2, '0');
      const m = d.getMinutes().toString().padStart(2, '0');
      const s = d.getSeconds().toString().padStart(2, '0');
      if (config.range === '1m' || config.range === '10m') {
        return `${h}:${m}:${s}`;
      }
      return `${h}:${m}`;
    };

    const formattedLabels = sortedTimestamps.map(formatTime);

    let rawPublish = sortedTimestamps.map((t) => publishMap.get(t) ?? stats.publishRate ?? 0);
    let rawDeliver = sortedTimestamps.map((t) => deliverMap.get(t) ?? stats.deliverManualRate ?? 0);
    let rawAck = sortedTimestamps.map((t) => ackMap.get(t) ?? stats.consumerAckRate ?? 0);
    let rawRedeliver = sortedTimestamps.map((t) => redeliverMap.get(t) ?? stats.redeliveredRate ?? 0);

    if (config.display === 'moving_average') {
      rawPublish = computeMovingAverage(rawPublish);
      rawDeliver = computeMovingAverage(rawDeliver);
      rawAck = computeMovingAverage(rawAck);
      rawRedeliver = computeMovingAverage(rawRedeliver);
    }

    const peak = Math.max(
      0,
      ...rawPublish,
      ...rawDeliver,
      ...rawAck,
      ...rawRedeliver,
      stats.publishRate || 0,
      stats.deliverManualRate || 0,
      stats.consumerAckRate || 0,
      stats.redeliveredRate || 0,
    );

    let calculatedMax = 0.8;
    let calculatedStep = 0.2;

    if (peak <= 0.2) {
      calculatedMax = 0.2;
      calculatedStep = 0.1;
    } else if (peak <= 0.4) {
      calculatedMax = 0.4;
      calculatedStep = 0.1;
    } else if (peak <= 0.8) {
      calculatedMax = 0.8;
      calculatedStep = 0.2;
    } else if (peak <= 1.5) {
      calculatedMax = 2.0;
      calculatedStep = 0.5;
    } else if (peak <= 3.5) {
      calculatedMax = 4.0;
      calculatedStep = 1.0;
    } else if (peak <= 8) {
      calculatedMax = Math.ceil(peak * 1.25);
      calculatedStep = 2.0;
    } else if (peak <= 25) {
      calculatedMax = Math.ceil((peak * 1.2) / 5) * 5;
      calculatedStep = 5.0;
    } else {
      calculatedMax = Math.ceil(peak * 1.15);
      calculatedStep = Math.max(1, Math.ceil(calculatedMax / 5));
    }

    return {
      labels: formattedLabels,
      publishData: rawPublish,
      deliverData: rawDeliver,
      ackData: rawAck,
      redeliverData: rawRedeliver,
      yMax: calculatedMax,
      yStepSize: calculatedStep,
    };
  }, [
    deliverSamples,
    ackSamples,
    publishSamples,
    redeliverSamples,
    stats.deliverManualRate,
    stats.consumerAckRate,
    stats.publishRate,
    stats.redeliveredRate,
    config.range,
    config.display,
  ]);

  const isMovingAverage = config.display === 'moving_average';

  const createGradient = (ctx, chartArea, r, g, b) => {
    if (!chartArea) return `rgba(${r}, ${g}, ${b}, 0.1)`;
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.24)`);
    gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, 0.03)`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    return gradient;
  };

  const data = {
    labels,
    datasets: [
      {
        label: isMovingAverage ? 'Publish Avg' : 'Publish',
        data: publishData,
        borderColor: '#f59e0b',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 245, 158, 11);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: isMovingAverage ? 'Deliver Avg' : 'Deliver',
        data: deliverData,
        borderColor: '#0284c7',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 2, 132, 199);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: isMovingAverage ? 'Ack Avg' : 'Ack',
        data: ackData,
        borderColor: '#10b981',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 16, 185, 129);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: isMovingAverage ? 'Redelivered Avg' : 'Redelivered',
        data: redeliverData,
        borderColor: '#a855f7',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 168, 85, 247);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: {
      duration: 300,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${Number(context.raw || 0).toFixed(2)} /s`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#94a3b8',
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: yMax,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [4, 4],
        },
        ticks: {
          stepSize: yStepSize,
          color: '#94a3b8',
          font: { size: 10 },
          callback: (value) => `${Number(value).toFixed(yMax <= 10 ? 1 : 0)} /s`,
        },
      },
    },
  };

  const rateItems = [
    { label: 'Publish', rate: stats.publishRate || 0, color: '#f59e0b' },
    { label: 'Deliver (manual ack)', rate: stats.deliverManualRate || 0, color: '#0284c7' },
    { label: 'Deliver (auto ack)', rate: stats.deliverAutoRate || 0, color: '#ef4444' },
    { label: 'Consumer ack', rate: stats.consumerAckRate || 0, color: '#10b981' },
    { label: 'Redelivered', rate: stats.redeliveredRate || 0, color: '#a855f7' },
    { label: 'Get (manual ack)', rate: stats.getManualRate || 0, color: '#3b82f6' },
    { label: 'Get (auto ack)', rate: stats.getAutoRate || 0, color: '#ec4899' },
    { label: 'Get (empty)', rate: stats.getEmptyRate || 0, color: '#64748b' },
  ];

  return (
    <Card sx={{ p: 2.5 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Message rates
          </Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={onOpenConfig}
            endIcon={<SlidersHorizontal style={{ width: 12, height: 12, color: '#006951' }} />}
            sx={{
              py: 0.2,
              px: 1,
              fontSize: '0.7rem',
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { borderColor: '#006951', color: '#006951' },
            }}
          >
            {rangeLabels[config.range] || 'last minute'}
          </Button>

          {isMovingAverage && (
            <Chip
              icon={<TrendingUp style={{ width: 12, height: 12, color: '#7c3aed' }} />}
              label="Moving Avg"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'rgba(124, 58, 237, 0.1)',
                color: '#7c3aed',
              }}
            />
          )}

          <IconButton
            size="small"
            onClick={onOpenConfig}
            sx={{
              width: 20,
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            ?
          </IconButton>
        </Stack>
      </Stack>

      {/* Main Content */}
      <Grid container spacing={3} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12} lg={8}>
          <Box
            sx={{
              height: chartHeights[config.size] || 240,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {config.display === 'value' ? (
              <Stack alignItems="center" justifyContent="center" height="100%">
                <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700 }}>
                  Current Deliver Rate
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, color: 'text.primary', my: 1 }}>
                  {Number(stats.deliverManualRate || 0).toFixed(2)} /s
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>
                    Ack: {Number(stats.consumerAckRate || 0).toFixed(2)} /s
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                    Publish: {Number(stats.publishRate || 0).toFixed(2)} /s
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Line data={data} options={options} />
            )}
          </Box>
        </Grid>

        {/* Right side rate items */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={1}>
            {rateItems.map((item, idx) => (
              <Grid item xs={6} key={idx}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.8} sx={{ minWidth: 0, pr: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }} noWrap>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>
                    {Number(item.rate || 0).toFixed(2)} <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>/s</span>
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};
